import { useEffect } from 'react';

import { useRPCQuery } from '@/lib/api/request/use-rpc-query';

export type LegalDocumentItem = {
  key: string;
  title: string;
  url: string;
  version: string;
};

export type GetLegalDocumentsResponse = {
  list: LegalDocumentItem[];
};

type GetLegalDocumentsRequest = Record<string, never>;

const SESSION_STORAGE_KEY = 'tmg_legal_documents';

const STATIC_DOCS: Record<string, string> = {
  privacy_policy:
    'https://1a683db7-85e9-4e7f-973f-dc8bd135ccc2.selstorage.ru/01_Politika_zashchity_i_obrabotki_PD_red_07.08.2026.docx.pdf',
  personal_data_consent:
    'https://1a683db7-85e9-4e7f-973f-dc8bd135ccc2.selstorage.ru/02_Soglasie_na_obrabotku_PD_red_07.08.2026.docx.pdf',
  mailing_consent:
    'https://1a683db7-85e9-4e7f-973f-dc8bd135ccc2.selstorage.ru/03_Soglasie_na_rassylku_red_07.08.2026.docx.pdf',
  recommendation_rules:
    'https://1a683db7-85e9-4e7f-973f-dc8bd135ccc2.selstorage.ru/04_Pravila_rekomendatelnykh_tekhnologiy_red_07.08.2026.docx.pdf',
  captcha_consent:
    'https://1a683db7-85e9-4e7f-973f-dc8bd135ccc2.selstorage.ru/05_Soglasie_Yandex_SmartCaptcha_red_07.08.2026.docx.pdf',
  metrics_consent:
    'https://1a683db7-85e9-4e7f-973f-dc8bd135ccc2.selstorage.ru/06_Soglasie_metricheskie_programmy_NOVYY.docx.pdf',
  public_offer:
    'https://1a683db7-85e9-4e7f-973f-dc8bd135ccc2.selstorage.ru/07_Oferta_red_07.08.2026.docx.pdf',
  bonus_system_rules:
    'https://1a683db7-85e9-4e7f-973f-dc8bd135ccc2.selstorage.ru/11_Polozhenie_o_bonusnoy_programme_NOVAYA_RED.docx.pdf',
  personal_data_distribution_consent:
    'https://1a683db7-85e9-4e7f-973f-dc8bd135ccc2.selstorage.ru/12_Soglasie_na_rasprostranenie_PD_NOVYY.docx.pdf',
};

function getSessionDocuments(): Record<string, string> | null {
  try {
    const cached = globalThis.sessionStorage?.getItem(SESSION_STORAGE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

export function useLegalDocuments() {
  const query = useRPCQuery<GetLegalDocumentsResponse, GetLegalDocumentsRequest>({
    method: useLegalDocuments.method,
    params: {},
    queryKey: ['legal-documents', 'ignoreLoading'],
  });

  useEffect(() => {
    if (query.data?.list) {
      try {
        const docsMap: Record<string, string> = {};
        for (const doc of query.data.list) {
          docsMap[doc.key] = doc.url;
        }
        globalThis.sessionStorage?.setItem(SESSION_STORAGE_KEY, JSON.stringify(docsMap));
      } catch (error) {
        console.error('Failed to save legal documents to sessionStorage', error);
      }
    }
  }, [query.data?.list]);

  const cachedDocs = getSessionDocuments();

  const getDocUrl = (key: string): string => {
    const foundDoc = query.data?.list?.find((d) => d.key === key);
    return foundDoc?.url || cachedDocs?.[key] || STATIC_DOCS[key] || '';
  };

  return {
    ...query,
    list: query.data?.list || [],
    getDocUrl,
  };
}

useLegalDocuments.method = 'system:get-legal-documents';
